<template>
  <div class="cdn-domain">
    <el-alert
      title="只有「状态生效」且「排序最小」的域名会被使用；切换域名请把目标域名的排序调到最小"
      type="warning"
      :closable="false"
      show-icon
      class="warn-tip">
    </el-alert>

    <!-- 工具栏 -->
    <el-form :inline="true" class="toolbar" @submit.native.prevent>
      <el-form-item>
        <el-input
          v-model="query.domain"
          placeholder="按域名搜索"
          clearable
          style="width: 240px;"
          @keyup.enter.native="handleSearch" />
      </el-form-item>
      <el-form-item>
        <el-select v-model="query.status" placeholder="全部状态" clearable style="width: 140px;">
          <el-option label="生效" :value="1" />
          <el-option label="未生效" :value="0" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleSearch">查询</el-button>
        <el-button type="success" @click="openDialog()">新增域名</el-button>
      </el-form-item>
    </el-form>

    <!-- 列表 -->
    <el-table :data="dataList" border v-loading="loading" style="width: 100%;">
      <el-table-column prop="sort" label="排序" width="90" align="center" />
      <el-table-column prop="domain" label="域名" min-width="260" show-overflow-tooltip />
      <el-table-column label="状态" width="120" align="center">
        <template slot-scope="scope">
          <el-tag :type="scope.row.status === 1 ? 'success' : 'info'" size="small">
            {{ scope.row.status === 1 ? '生效' : '未生效' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="remark" label="备注" min-width="160" show-overflow-tooltip />
      <el-table-column prop="createDate" label="创建时间" width="170" align="center" />
      <el-table-column prop="updateDate" label="更新时间" width="170" align="center">
        <template slot-scope="scope">{{ scope.row.updateDate || '—' }}</template>
      </el-table-column>
      <el-table-column label="操作" width="220" align="center" fixed="right">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="openDialog(scope.row)">编辑</el-button>
          <el-button
            type="text"
            size="small"
            @click="toggleStatus(scope.row)">
            {{ scope.row.status === 1 ? '停用' : '生效' }}
          </el-button>
          <el-button type="text" size="small" style="color:#f56c6c;" @click="handleDelete(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <el-pagination
      class="pagination"
      background
      layout="total, prev, pager, next, sizes"
      :total="total"
      :current-page="query.page"
      :page-size="query.limit"
      :page-sizes="[10, 20, 50]"
      @current-change="handlePageChange"
      @size-change="handleSizeChange" />

    <!-- 新增/编辑弹窗 -->
    <el-dialog :title="form.id ? '编辑域名' : '新增域名'" :visible.sync="dialogVisible" width="520px" :close-on-click-modal="false">
      <el-form :model="form" :rules="rules" ref="form" label-width="90px">
        <el-form-item label="域名" prop="domain">
          <el-input v-model="form.domain" placeholder="如 https://cdn.example.com" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">生效</el-radio>
            <el-radio :label="0">未生效</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="排序" prop="sort">
          <el-input-number v-model="form.sort" :min="0" :max="999999" />
          <span class="hint">值越小越靠前</span>
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input v-model="form.remark" type="textarea" :rows="2" placeholder="选填" />
        </el-form-item>
      </el-form>
      <template slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submit">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import {
  getCdnDomainPage,
  addCdnDomain,
  updateCdnDomain,
  updateCdnDomainStatus,
  deleteCdnDomain
} from '@/api/cdnDomain'

export default {
  name: 'CdnDomain',
  data () {
    return {
      loading: false,
      submitting: false,
      dataList: [],
      total: 0,
      query: {
        page: 1,
        limit: 10,
        domain: '',
        status: ''
      },
      dialogVisible: false,
      form: {
        id: '',
        domain: '',
        status: 1,
        sort: 0,
        remark: ''
      },
      rules: {
        domain: [{ required: true, message: '请输入域名', trigger: 'blur' }],
        status: [{ required: true, message: '请选择状态', trigger: 'change' }]
      }
    }
  },
  created () {
    this.fetchList()
  },
  methods: {
    fetchList () {
      this.loading = true
      const params = {
        page: this.query.page,
        limit: this.query.limit,
        domain: this.query.domain || undefined,
        status: this.query.status === '' ? undefined : this.query.status
      }
      getCdnDomainPage(params).then(({ data: res }) => {
        if (res.code === 0) {
          this.dataList = res.data.list || []
          this.total = res.data.total || 0
        } else {
          this.$message.error(res.msg || '加载失败')
        }
      }).finally(() => {
        this.loading = false
      })
    },
    handleSearch () {
      this.query.page = 1
      this.fetchList()
    },
    handlePageChange (page) {
      this.query.page = page
      this.fetchList()
    },
    handleSizeChange (size) {
      this.query.limit = size
      this.query.page = 1
      this.fetchList()
    },
    openDialog (row) {
      if (row) {
        this.form = { id: row.id, domain: row.domain, status: row.status, sort: row.sort, remark: row.remark || '' }
      } else {
        this.form = { id: '', domain: '', status: 1, sort: 0, remark: '' }
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate()
      })
    },
    submit () {
      this.$refs.form.validate((valid) => {
        if (!valid) return
        this.submitting = true
        const api = this.form.id ? updateCdnDomain : addCdnDomain
        api(this.form).then(({ data: res }) => {
          if (res.code === 0) {
            this.$message.success('保存成功')
            this.dialogVisible = false
            this.fetchList()
          } else {
            this.$message.error(res.msg || '保存失败')
          }
        }).finally(() => {
          this.submitting = false
        })
      })
    },
    toggleStatus (row) {
      const next = row.status === 1 ? 0 : 1
      updateCdnDomainStatus(row.id, next).then(({ data: res }) => {
        if (res.code === 0) {
          row.status = next
          this.$message.success('已更新')
        } else {
          this.$message.error(res.msg || '操作失败')
        }
      })
    },
    handleDelete (row) {
      this.$confirm(`确定删除域名「${row.domain}」？`, '提示', { type: 'warning' }).then(() => {
        deleteCdnDomain([row.id]).then(({ data: res }) => {
          if (res.code === 0) {
            this.$message.success('删除成功')
            this.fetchList()
          } else {
            this.$message.error(res.msg || '删除失败')
          }
        })
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.cdn-domain { padding: 16px; }
.warn-tip { margin-bottom: 16px; }
.toolbar { margin-bottom: 12px; }
.pagination { margin-top: 16px; text-align: right; }
.hint { margin-left: 8px; color: #909399; font-size: 12px; }
</style>
